/**
 * Accounts List Page
 * 
 * Unified view of all accounts (advertisers with optional client context).
 * Replaces the separate Clients and Advertisers list pages in navigation.
 */

import { useState, useCallback } from 'react';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Badge } from '@/components/ui/badge';
import { Loader2, MoreVertical } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent } from '@/components/ui/card';
import { useLocation } from 'wouter';
import * as accountsApi from '@/api/accounts';
import type { Account } from '@/api/accounts';
import { AdvertiserForm } from '@/components/admin/AdvertiserForm';

export default function AccountsPage() {
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearchTerm = useDebounce(searchTerm, 300);
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingAccount, setEditingAccount] = useState<Account | null>(null);
    const queryClient = useQueryClient();
    const [, setLocation] = useLocation();

    // Fetch accounts
    const {
        data: accounts = [],
        isLoading,
        error,
    } = useQuery({
        queryKey: ['accounts', debouncedSearchTerm],
        queryFn: () => accountsApi.fetchAccounts(debouncedSearchTerm),
    });

    // Handle create
    const handleCreate = useCallback(
        async (formData: {
            name: string;
            address?: string;
            contactName?: string;
            contactEmail?: string;
            contactPhone?: string;
            clientId?: number | null;
        }) => {
            try {
                await accountsApi.createAccount(formData);
                setIsCreateDialogOpen(false);
                queryClient.invalidateQueries({ queryKey: ['accounts'] });
            } catch (error: any) {
                throw error;
            }
        },
        [queryClient]
    );

    // Handle update
    const handleUpdate = useCallback(
        async (formData: {
            name: string;
            address?: string;
            contactName?: string;
            contactEmail?: string;
            contactPhone?: string;
            clientId?: number | null;
        }) => {
            if (!editingAccount?.id) return;

            try {
                await accountsApi.updateAccount(editingAccount.id, formData);
                setEditingAccount(null);
                queryClient.invalidateQueries({ queryKey: ['accounts'] });
            } catch (error: any) {
                throw error;
            }
        },
        [editingAccount?.id, queryClient]
    );

    // Handle delete
    const handleDelete = useCallback(
        async (id: number) => {
            try {
                await accountsApi.deleteAccount(id);
                queryClient.invalidateQueries({ queryKey: ['accounts'] });
            } catch (error: any) {
                throw error;
            }
        },
        [queryClient]
    );

    // Navigate to account detail
    const handleView = (account: Account) => {
        setLocation(`/accounts/${account.id}`);
    };

    return (
        <div className="container mx-auto p-6 space-y-6">
            <PageHeader
                icon={Building2}
                title="Accounts"
                description="Manage your commercial accounts and their associated bookings and campaigns"
            >
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Account
                </Button>
            </PageHeader>

            {/* Error State */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 text-red-700">
                    Failed to load accounts. Please try again.
                </div>
            )}

            {/* Search and List */}
            <Card>
                <CardContent className="pt-6">
                    {/* Search */}
                    <div className="relative mb-6">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Search accounts..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                        />
                    </div>

                    {/* Table */}
                    {isLoading ? (
                        <div className="flex items-center justify-center p-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : accounts.length === 0 ? (
                        <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                            <Building2 className="h-10 w-10 mb-4 opacity-20" />
                            <p>No accounts found.</p>
                            {searchTerm && (
                                <p className="text-sm mt-1">
                                    Try adjusting your search or{' '}
                                    <button
                                        className="text-blue-600 hover:underline"
                                        onClick={() => setSearchTerm('')}
                                    >
                                        clear the search
                                    </button>
                                </p>
                            )}
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Account Name</TableHead>
                                    <TableHead>Contact</TableHead>
                                    <TableHead>Billing Entity</TableHead>
                                    <TableHead>Campaigns</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-[70px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map((account) => (
                                    <TableRow key={account.id}>
                                        <TableCell>
                                            <button
                                                className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left"
                                                onClick={() => handleView(account)}
                                            >
                                                {account.name}
                                            </button>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {account.contactEmail || account.contactName || '—'}
                                        </TableCell>
                                        <TableCell>
                                            {account.clientName ? (
                                                <span className="text-sm">{account.clientName}</span>
                                            ) : (
                                                <span className="text-muted-foreground italic text-sm">Not linked</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {account.campaignCount !== undefined ? (
                                                <Badge variant="secondary">{account.campaignCount}</Badge>
                                            ) : (
                                                '—'
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={account.isActive ? 'default' : 'secondary'}>
                                                {account.isActive ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="ghost" size="sm">
                                                        <MoreVertical className="h-4 w-4" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem onClick={() => handleView(account)}>
                                                        View Details
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => setEditingAccount(account)}>
                                                        Edit
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem
                                                        className="text-red-600"
                                                        onClick={() => {
                                                            if (confirm('Are you sure you want to delete this account?')) {
                                                                handleDelete(account.id);
                                                            }
                                                        }}
                                                    >
                                                        Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Create Dialog */}
            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Account</DialogTitle>
                        <DialogDescription>
                            Add a new account. You can link it to a billing entity and create bookings later.
                        </DialogDescription>
                    </DialogHeader>
                    <AdvertiserForm
                        onSubmit={handleCreate}
                        onCancel={() => setIsCreateDialogOpen(false)}
                    />
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={editingAccount !== null} onOpenChange={(open) => !open && setEditingAccount(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Account</DialogTitle>
                        <DialogDescription>
                            Update the account details below.
                        </DialogDescription>
                    </DialogHeader>
                    {editingAccount && (
                        <AdvertiserForm
                            initialData={editingAccount}
                            onSubmit={handleUpdate}
                            onCancel={() => setEditingAccount(null)}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
